import axios from 'axios'
import { fetchAuthSession } from 'aws-amplify/auth'

const API_BASE_URL = import.meta.env.VITE_API_ENDPOINT || 'https://api.softitect.com'

// Create axios instance
const api = axios.create({
  baseURL: API_BASE_URL,
  headers: {
    'Content-Type': 'application/json',
  },
})

// Add auth token to requests
api.interceptors.request.use(async (config) => {
  try {
    const session = await fetchAuthSession()
    const token = session.tokens?.idToken?.toString()
    if (token) {
      config.headers.Authorization = `Bearer ${token}`
    }
  } catch (error) {
    console.error('Failed to get auth token:', error)
  }
  return config
})

// Response interceptor for error handling
api.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response?.status === 401) {
      // Handle unauthorized - redirect to login
      window.location.href = '/login'
    }
    return Promise.reject(error)
  }
)

export interface UserProfile {
  userId: string
  cognitoId: string
  email: string
  userTier: 'free' | 'builder' | 'launch' | 'growth' | 'scale'
  apiKeyId?: string
  firstName?: string
  lastName?: string
  companyName?: string
  usageDescription?: string
  createdAt: string
  updatedAt: string
}

export interface ApiKeyInfo {
  hasApiKey: boolean
  apiKeys: ApiKeyItem[]
  userTier: string
}

export interface ApiKeyItem {
  apiKeyId: string
  title: string
  createDate: string
  isActive: boolean
}

export interface ApiKeyResponse {
  apiKey: string
  apiKeyId: string
  title: string
  message: string
}

export interface AnalyticsData {
  userId: string
  userTier: string
  analytics: {
    totalRequests: number
    requestsThisMonth: number
    requestsToday: number
    lastRequestAt: string
    topEndpoints: Array<{
      endpoint: string
      count: number
    }>
    errorRate: number
    avgResponseTime: number
  }
  timestamp: string
}

export interface PricingPlan {
  tierId: string
  planName: string
  monthlyPrice: number
  creditLimit: number
  features: string[]
  paddlePriceId: string
  displayOrder: number
  description: string
  isPopular: boolean
  currency: string
  annualPrice?: number
  annualPaddlePriceId?: string
}

// Public API endpoints (no auth required)
export const publicApi = {
  // New signup flow
  signUp: async (
    email: string,
    password: string,
    firstName: string,
    lastName: string,
    companyName?: string
  ): Promise<{ message: string; userId: string; email: string }> => {
    const response = await api.post('/api/v1/public/signup', {
      email,
      password,
      firstName,
      lastName,
      companyName
    })
    return response.data
  },

  verifySignUp: async (
    email: string,
    code: string
  ): Promise<{ message: string; email: string }> => {
    const response = await api.post('/api/v1/public/signup/verify', {
      email,
      code
    })
    return response.data
  },

  completeSignUp: async (
    userId: string,
    email: string,
    firstName: string,
    lastName: string,
    companyName: string,
    usageDescription: string
  ): Promise<{ message: string; user: UserProfile }> => {
    const response = await api.post('/api/v1/public/signup/complete', {
      userId,
      email,
      firstName,
      lastName,
      companyName,
      usageDescription
    })
    return response.data
  },

  resendVerificationCode: async (email: string): Promise<{ message: string }> => {
    const response = await api.post('/api/v1/public/signup/resend', {
      email
    })
    return response.data
  },

  // Legacy registration endpoint
  registerUser: async (
    userId: string,
    email: string,
    firstName?: string,
    lastName?: string,
    companyName?: string,
    usageDescription?: string
  ): Promise<{ message: string }> => {
    const response = await api.post('/api/v1/public/register', {
      userId,
      email,
      firstName,
      lastName,
      companyName,
      usageDescription
    })
    return response.data
  },

  getPricingPlans: async (): Promise<PricingPlan[]> => {
    try {
      const response = await api.get('/api/v1/public/pricing-plans')
      return response.data
    } catch (error) {
      console.error('Failed to fetch pricing plans:', error)
      // Fallback to default plans if API call fails
      return [
        {
          tierId: 'builder',
          planName: 'Builder',
          monthlyPrice: 9,
          creditLimit: 300,
          features: ['300 credits per month', 'All models access'],
          paddlePriceId: '',
          displayOrder: 2,
          description: 'For personal projects and experimentation',
          isPopular: false,
          currency: 'USD'
        },
        {
          tierId: 'launch',
          planName: 'Launch',
          monthlyPrice: 39,
          creditLimit: 4000,
          features: ['4,000 credits per month', 'All models access'],
          paddlePriceId: '',
          displayOrder: 3,
          description: 'For professionals and growing teams',
          isPopular: true,
          currency: 'USD'
        },
        {
          tierId: 'growth',
          planName: 'Growth',
          monthlyPrice: 99,
          creditLimit: 15000,
          features: ['15,000 credits per month', 'All models access'],
          paddlePriceId: '',
          displayOrder: 4,
          description: 'For large-scale applications',
          isPopular: false,
          currency: 'USD'
        },
        {
          tierId: 'scale',
          planName: 'Scaled',
          monthlyPrice: 349,
          creditLimit: 75000,
          features: ['75,000 credits per month', 'All models access'],
          paddlePriceId: '',
          displayOrder: 5,
          description: 'For enterprise-scale deployments',
          isPopular: false,
          currency: 'USD'
        }
      ]
    }
  },
}

// User API endpoints
export const userApi = {
  getProfile: async (): Promise<UserProfile> => {
    const response = await api.get('/api/v1/user')
    return response.data
  },

  updateProfile: async (updates: Partial<UserProfile>): Promise<UserProfile> => {
    const response = await api.put('/api/v1/user', updates)
    return response.data
  },

  getApiKeyInfo: async (): Promise<ApiKeyInfo> => {
    const response = await api.get('/api/v1/user/api-key')
    return response.data
  },

  generateApiKey: async (title?: string): Promise<ApiKeyResponse> => {
    const response = await api.post('/api/v1/user/api-key', { title })
    return response.data
  },

  revokeApiKey: async (apiKeyId: string): Promise<{ message: string }> => {
    const response = await api.delete(`/api/v1/user/api-key/${apiKeyId}`)
    return response.data
  },

  changePassword: async (oldPassword: string, newPassword: string): Promise<{ message: string; success: boolean }> => {
    const response = await api.post('/api/v1/user/password', {
      oldPassword,
      newPassword,
    })
    return response.data
  },

  getSubscriptionStatus: async (): Promise<{
    userTier: string
    subscriptionStatus: string | null
    scheduledCancellation: boolean
    scheduledChangeType: 'upgrade' | 'downgrade' | 'cancel' | null
    scheduledChangeTier: 'builder' | 'launch' | 'growth' | 'scale' | null
    scheduledChangeEffectiveDate: string | null
    paddleSubscriptionId: string | null
    subscriptionStartDate: string | null
    subscriptionEndDate: string | null
  }> => {
    const response = await api.get('/api/v1/user/subscription')
    return response.data
  },

  cancelSubscription: async (): Promise<{ message: string; subscriptionId: string; accessUntil: string }> => {
    const response = await api.post('/api/v1/user/subscription/cancel')
    return response.data
  },

  upgradeSubscription: async (priceId: string): Promise<{ message: string; subscriptionId: string }> => {
    const response = await api.post('/api/v1/user/subscription/upgrade', { priceId })
    return response.data
  },

  downgradeSubscription: async (priceId: string): Promise<{ message: string; subscriptionId: string; effectiveDate: string }> => {
    const response = await api.post('/api/v1/user/subscription/downgrade', { priceId })
    return response.data
  },

  reactivateSubscription: async (): Promise<{ message: string; subscriptionId: string }> => {
    const response = await api.post('/api/v1/user/subscription/reactivate')
    return response.data
  },

  removeScheduledChange: async (): Promise<{ message: string; subscriptionId: string }> => {
    const response = await api.delete('/api/v1/user/subscription/scheduled-change')
    return response.data
  },

  getUsageSummary: async (): Promise<{
    monthKey: string
    totalRequests: number
    totalInputTokens: number
    totalOutputTokens: number
    totalErrors: number
    apiKeys: Array<{
      apiKeyId: string
      title: string
      isActive: boolean
      totalRequests: number
      totalInputTokens: number
      totalOutputTokens: number
      totalErrors: number
    }>
  }> => {
    const response = await api.get('/api/v1/user/usage/summary')
    return response.data
  },

  getCreditBalance: async (): Promise<{
    creditBalance: number
    creditLimit: number
    creditResetDate: string
    creditsUsedThisMonth: number
    userTier: string
    scheduledChangeTier: string | null
    scheduledChangeEffectiveDate: string | null
  }> => {
    const response = await api.get('/api/v1/user/credits')
    return response.data
  },
}

// Client API endpoints (for testing with API key)
export const clientApi = {
  getAnalytics: async (): Promise<AnalyticsData> => {
    const response = await api.get('/api/v1/client/analytics')
    return response.data
  },

  testApiKey: async (apiKey: string): Promise<any> => {
    const response = await api.get('/api/v1/client', {
      headers: {
        'X-Api-Key': apiKey,
      },
    })
    return response.data
  },

  chatCompletion: async (
    apiKey: string,
    modelId: string,
    message: string
  ): Promise<{
    id: string
    model: string
    choices: Array<{
      index: number
      message: {
        role: string
        content: string
      }
      finishReason: string
    }>
    usage: {
      promptTokens: number
      completionTokens: number
      totalTokens: number
    }
  }> => {
    const response = await api.post(
      '/api/v1/client/chat/completion',
      {
        modelId,
        message,
      },
      {
        headers: {
          'X-Api-Key': apiKey,
        },
      }
    )
    return response.data
  },
}

export default api